import * as React from "react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popper,
  ClickAwayListener,
  MenuList,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { CancellationResultCard, CancellationResult } from "../shared/CancellationResultCard";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1600px",
  },
  filtersContainer: {
    padding: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "flex-end",
  },
  filterItem: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    minWidth: 160,
  },
  filterItemWide: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    minWidth: 220,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 1300,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  tableCellSmall: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    color: theme.palette.grey[500],
  },
}));

const CATEGORY_OPTIONS = [
  { value: "", label: "All" },
  { value: "emailed_to_tillo", label: "Emailed to Tillo" },
  { value: "cancelled_via_tillo", label: "Cancelled via Tillo" },
  { value: "loss_absorbed", label: "Loss Absorbed" },
  { value: "other", label: "Other" },
];

const ALL_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "refund_failed", label: "Refund Failed" },
  { value: "declined", label: "Declined" },
];

const STATUS_BY_CATEGORY: Record<string, string[]> = {
  "": ["", "pending", "completed", "refund_failed", "declined"],
  emailed_to_tillo: ["", "pending", "declined"],
  cancelled_via_tillo: ["", "pending", "completed", "refund_failed"],
  loss_absorbed: ["", "pending", "completed", "refund_failed"],
  other: [""],
};

const getStatusChipColor = (status: string): "default" | "primary" | "secondary" => {
  switch (status) {
    case "completed": return "primary";
    case "refund_failed": return "secondary";
    case "declined": return "secondary";
    default: return "default";
  }
};

const CATEGORY_LABELS: Record<string, string> = {
  emailed_to_tillo: "Emailed to Tillo",
  cancelled_via_tillo: "Cancelled via Tillo",
  loss_absorbed: "Loss Absorbed",
  other: "Other",
};

const CATEGORY_COLORS: Record<string, string> = {
  emailed_to_tillo: "#e3f2fd",
  cancelled_via_tillo: "#f3e5f5",
  loss_absorbed: "#fff3e0",
  other: "#f5f5f5",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  completed: "Completed",
  refund_failed: "Refund Failed",
  declined: "Declined",
};

const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  return dateString.replace(/\.\d{3}Z$/, "").replace("T", " ");
};

const formatCurrency = (amount: number | string) => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(numAmount);
};

export const CancellationHistoryList = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [clientRefId, setClientRefId] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [assignee, setAssignee] = useState("");
  const [brand, setBrand] = useState("");
  const [cancDateFrom, setCancDateFrom] = useState("");
  const [cancDateTo, setCancDateTo] = useState("");

  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [brandSearch, setBrandSearch] = useState("");
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const brandAnchorRef = useRef<HTMLDivElement>(null);

  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<"completed" | "declined">("completed");
  const [dialogRecordId, setDialogRecordId] = useState<number | null>(null);
  const [dialogRef, setDialogRef] = useState<string>("");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogCancelToUser, setDialogCancelToUser] = useState(false);
  const [dialogUserAlreadyRefunded, setDialogUserAlreadyRefunded] = useState(false);
  const [dialogResult, setDialogResult] = useState<CancellationResult | null>(null);
  const [dialogDone, setDialogDone] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);

  const LIMIT = 20;

  const fetchAnalytics = async () => {
    try {
      // Analytics only responds to brand + cancellation date-range filters.
      // Other filters (status/category/assignee/clientRefId) are intentionally
      // excluded so the top-of-page totals stay consistent with how ops thinks
      // about the cancellation funnel.
      const params: any = { fetch_analytics: 'true' };
      if (brand) params.brand = brand;
      if (cancDateFrom) params.cancellation_date_from = cancDateFrom;
      if (cancDateTo) params.cancellation_date_to = cancDateTo;
      const config = getConfigGet(params);
      const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_CANCELLATION_HISTORY, config);
      setAnalytics(response.data?.analytics || null);
    } catch (err) {
      // Silent fail
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const config = getConfigGet({ fetch_brands: 'true' });
        const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_CANCELLATION_HISTORY, config);
        setBrandOptions(response.data?.brands || []);
      } catch (err) {
        // Silent fail
      }
      fetchAnalytics();
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    const validStatuses = STATUS_BY_CATEGORY[value] || STATUS_BY_CATEGORY[""];
    if (!validStatuses.includes(status)) {
      setStatus("");
    }
  };

  const availableStatuses = ALL_STATUS_OPTIONS.filter(
    (opt) => (STATUS_BY_CATEGORY[category] || STATUS_BY_CATEGORY[""]).includes(opt.value)
  );

  const filteredBrands = brandOptions.filter((b) =>
    b.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const buildQueryParams = (skip: number) => {
    const params: any = { $limit: LIMIT, $skip: skip };
    if (clientRefId.trim()) params.client_reference_id = clientRefId.trim();
    if (status) params.status = status;
    if (category) params.category = category;
    if (assignee.trim()) params.assignee = assignee.trim();
    if (brand) params.brand = brand;
    if (cancDateFrom) params.cancellation_date_from = cancDateFrom;
    if (cancDateTo) params.cancellation_date_to = cancDateTo;
    return params;
  };

  const fetchRecords = async (skip: number = 0, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setRecords([]);
    }

    try {
      const params = buildQueryParams(skip);
      const config = getConfigGet(params);
      const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_CANCELLATION_HISTORY, config);

      const data = response.data.data || [];
      const total = response.data.total || 0;

      if (isLoadMore) {
        setRecords((prev) => [...prev, ...data]);
      } else {
        setRecords(data);
      }

      setTotalCount(total);
      setCurrentSkip(skip);
      setHasSearched(true);

      const totalLoaded = isLoadMore ? records.length + data.length : data.length;
      setHasMoreData(totalLoaded < total);

      if (data.length === 0 && !isLoadMore) {
        notify("No records found", "info");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to fetch records";
      notify(errorMessage, "error");
      if (!isLoadMore) setRecords([]);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    setCurrentSkip(0);
    setHasMoreData(false);
    fetchRecords(0, false);
    fetchAnalytics();
  };

  const handleLoadMore = () => {
    fetchRecords(currentSkip + LIMIT, true);
  };

  const handleClearFilters = () => {
    setClientRefId("");
    setStatus("");
    setCategory("");
    setAssignee("");
    setBrand("");
    setBrandSearch("");
    setCancDateFrom("");
    setCancDateTo("");
  };

  const openActionDialog = (recordId: number, action: "completed" | "declined", reference: string, userAlreadyRefunded: boolean = false) => {
    setDialogRecordId(recordId);
    setDialogRef(reference);
    setDialogAction(action);
    setDialogCancelToUser(false);
    setDialogUserAlreadyRefunded(userAlreadyRefunded);
    setDialogResult(null);
    setDialogDone(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    if (dialogDone) {
      fetchRecords(0, false);
      fetchAnalytics();
    }
  };

  const handleConfirmAction = async () => {
    if (!dialogRecordId) return;
    setDialogLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        `${ENDPOINTS.GIFT_CARD_CANCELLATION_HISTORY}/${dialogRecordId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            action: dialogAction,
            ...(dialogCancelToUser ? { cancel_to_user: 'true' } : {}),
          },
        }
      );

      setDialogDone(true);

      if (dialogCancelToUser && response.data?.data) {
        setDialogResult({
          success: response.data.success,
          message: response.data.message,
          data: response.data.data,
        });
      } else {
        setDialogResult({
          success: true,
          message: response.data?.message || `Record marked as '${dialogAction}' successfully.`,
        });
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to update record";
      setDialogDone(true);
      setDialogResult({ success: false, message: errorMessage });
    } finally {
      setDialogLoading(false);
    }
  };

  return (
    <div className={classes.main}>
      <Card className={classes.card}>
        <CardContent>
          <Box style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <Typography variant="h5" component="h1">
              Gift Card Cancellation History
            </Typography>
            <IconButton size="small" onClick={() => setInfoOpen(true)} style={{ marginLeft: 8 }} title="Category & Status Guide">
              <InfoOutlinedIcon style={{ color: "#1976d2" }} />
            </IconButton>
          </Box>

          {analytics && (
            <Box style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <Paper elevation={2} style={{ flex: 1, minWidth: 200, padding: "16px 20px", borderLeft: "4px solid #1976d2" }}>
                <Typography variant="caption" style={{ color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Total Requests
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#1976d2", marginTop: 4 }}>
                  {Number(analytics.total_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" style={{ color: "#555", marginTop: 2 }}>
                  £{Number(analytics.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Paper>

              <Paper elevation={2} style={{ flex: 1, minWidth: 200, padding: "16px 20px", borderLeft: "4px solid #ff9800" }}>
                <Typography variant="caption" style={{ color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Pending with Tillo
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#ff9800", marginTop: 4 }}>
                  {Number(analytics.pending_tillo_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" style={{ color: "#555", marginTop: 2 }}>
                  £{Number(analytics.pending_tillo_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Paper>

              <Paper elevation={2} style={{ flex: 1, minWidth: 200, padding: "16px 20px", borderLeft: "4px solid #4caf50" }}>
                <Typography variant="caption" style={{ color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Successfully Cancelled
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#4caf50", marginTop: 4 }}>
                  {Number(analytics.success_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" style={{ color: "#555", marginTop: 2 }}>
                  £{Number(analytics.success_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Paper>

              <Paper elevation={2} style={{ flex: 1, minWidth: 200, padding: "16px 20px", borderLeft: "4px solid #e53935" }}>
                <Typography variant="caption" style={{ color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Absorbed by Sprive
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#e53935", marginTop: 4 }}>
                  {Number(analytics.absorbed_count || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" style={{ color: "#555", marginTop: 2 }}>
                  £{Number(analytics.absorbed_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Filters Row 1 */}
          <div className={classes.filtersContainer}>
            <TextField
              className={classes.filterItemWide}
              label="Client Reference ID"
              value={clientRefId}
              onChange={(e) => setClientRefId(e.target.value)}
              variant="outlined"
              size="small"
            />
            <FormControl variant="outlined" size="small" className={classes.filterItem}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value as string)}
                label="Category"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterItem}
              disabled={availableStatuses.length <= 1}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={availableStatuses.length <= 1 ? "" : status}
                onChange={(e) => setStatus(e.target.value as string)}
                label="Status"
                disabled={availableStatuses.length <= 1}
              >
                {availableStatuses.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Brand searchable dropdown */}
            <div ref={brandAnchorRef} style={{ position: "relative" }} className={classes.filterItem}>
              <TextField
                label="Brand"
                value={brand || brandSearch}
                onChange={(e) => {
                  setBrandSearch(e.target.value);
                  setBrand("");
                  setBrandDropdownOpen(true);
                }}
                onFocus={() => setBrandDropdownOpen(true)}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {brand ? (
                        <span
                          style={{ cursor: "pointer", fontSize: "0.8rem", color: "#999" }}
                          onClick={() => { setBrand(""); setBrandSearch(""); }}
                        >
                          x
                        </span>
                      ) : (
                        <SearchIcon style={{ fontSize: "1rem", color: "#999" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Popper
                open={brandDropdownOpen && filteredBrands.length > 0}
                anchorEl={brandAnchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: brandAnchorRef.current?.offsetWidth || 160 }}
              >
                <ClickAwayListener onClickAway={() => setBrandDropdownOpen(false)}>
                  <Paper style={{ maxHeight: 200, overflow: "auto" }}>
                    <MenuList dense>
                      {filteredBrands.map((b) => (
                        <MenuItem
                          key={b}
                          onClick={() => {
                            setBrand(b);
                            setBrandSearch("");
                            setBrandDropdownOpen(false);
                          }}
                          selected={brand === b}
                        >
                          {b}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </div>

            <TextField
              className={classes.filterItem}
              label="Assignee"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              variant="outlined"
              size="small"
            />
          </div>

          {/* Filters Row 2: Dates + Buttons */}
          <div className={classes.filtersContainer}>
            <TextField
              className={classes.filterItem}
              label="Cancellation From"
              type="date"
              value={cancDateFrom}
              onChange={(e) => setCancDateFrom(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.filterItem}
              label="Cancellation To"
              type="date"
              value={cancDateTo}
              onChange={(e) => setCancDateTo(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              style={{ marginRight: 8, marginBottom: 8 }}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              style={{ marginBottom: 8 }}
            >
              Clear
            </Button>
          </div>

          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Loading cancellation history...
              </Typography>
            </div>
          )}

          {hasSearched && !loading && records.length === 0 && (
            <div className={classes.noDataContainer}>
              <Typography variant="h6">No records found</Typography>
            </div>
          )}

          {records.length > 0 && (
            <>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell}>Reference</TableCell>
                      <TableCell className={classes.tableCell}>Brand</TableCell>
                      <TableCell className={classes.tableCell}>Amount</TableCell>
                      <TableCell className={classes.tableCellSmall}>Client Ref ID</TableCell>
                      <TableCell className={classes.tableCell}>Category</TableCell>
                      <TableCell className={classes.tableCell}>Status</TableCell>
                      <TableCell className={classes.tableCell}>Assignee</TableCell>
                      <TableCell className={classes.tableCell}>Cancellation Date</TableCell>
                      <TableCell className={classes.tableCell}>Purchase Date</TableCell>
                      <TableCell className={classes.tableCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record) => (
                      <TableRow key={record.client_reference_id} hover>
                        <TableCell className={classes.tableCell}>
                          {record.reference || "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>{record.brand || "N/A"}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatCurrency(record.amount)}
                        </TableCell>
                        <TableCell
                          className={classes.tableCellSmall}
                          title={record.client_reference_id}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigator.clipboard.writeText(record.client_reference_id || "");
                            notify("Client Reference ID copied", "info");
                          }}
                        >
                          {record.client_reference_id || "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Chip
                            label={CATEGORY_LABELS[record.display_category] || record.display_category || "N/A"}
                            size="small"
                            style={{
                              fontSize: "0.75rem",
                              backgroundColor: CATEGORY_COLORS[record.display_category] || "#f5f5f5",
                            }}
                          />
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Chip
                            label={STATUS_LABELS[record.display_status] || record.display_status || "N/A"}
                            size="small"
                            color={getStatusChipColor(record.display_status)}
                            style={{ fontSize: "0.75rem" }}
                          />
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.assignee || "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatDate(record.cancellation_date || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatDate(record.purchase_date || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.actionable_record_id ? (
                            <Box style={{ display: "flex" }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                style={{ marginRight: 4, fontSize: "0.7rem" }}
                                onClick={() => openActionDialog(record.actionable_record_id, "completed", record.reference, !!record.user_already_refunded)}
                              >
                                Mark Completed
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                style={{ fontSize: "0.7rem" }}
                                onClick={() => openActionDialog(record.actionable_record_id, "declined", record.reference, !!record.user_already_refunded)}
                              >
                                Declined
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.75rem" }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 16 }}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {records.length} out of {totalCount}
                </Typography>
                {hasMoreData && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Mark as {dialogAction === "completed" ? "Completed" : "Declined"}{" "}
          {dialogRef ? `(${dialogRef})` : ""}
        </DialogTitle>
        <DialogContent>
          {!dialogDone ? (
            <>
              <Box style={{
                padding: "12px 16px",
                backgroundColor: dialogAction === "completed" ? "#e8f5e9" : "#fce4ec",
                borderLeft: `4px solid ${dialogAction === "completed" ? "#4caf50" : "#e53935"}`,
                borderRadius: 4,
                marginBottom: 16,
              }}>
                <Typography variant="subtitle2" style={{
                  color: dialogAction === "completed" ? "#2e7d32" : "#c62828",
                  fontWeight: 700,
                  marginBottom: 4,
                }}>
                  {dialogAction === "completed"
                    ? "You are marking this as completed"
                    : "You are marking this as declined"}
                </Typography>
                <Typography variant="body2" style={{
                  color: dialogAction === "completed" ? "#33691e" : "#b71c1c",
                  lineHeight: 1.6,
                }}>
                  {dialogUserAlreadyRefunded
                    ? "The user has already been refunded. This will only update the email record status for housekeeping."
                    : dialogAction === "completed"
                    ? "This assumes Tillo has processed the cancellation on their end. The user has not been refunded yet — check the option below to process the refund now."
                    : "Tillo has declined (or will not process) this cancellation. You can still refund the user by checking the option below. The loss will be absorbed by Sprive."}
                </Typography>
              </Box>

              {dialogUserAlreadyRefunded ? (
                <Box style={{
                  background: "#e3f2fd",
                  padding: "8px 12px",
                  borderRadius: 4,
                  border: "1px solid #90caf9",
                  marginTop: 4,
                }}>
                  <Typography variant="body2" style={{ color: "#1565c0", fontWeight: 600 }}>
                    User already refunded — no further refund will be processed.
                  </Typography>
                </Box>
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dialogCancelToUser}
                      onChange={(e) => setDialogCancelToUser(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Cancel to User (process refund, mark purchase as cancelled, claw back rewards)"
                />
              )}
            </>
          ) : (
            dialogResult && <CancellationResultCard result={dialogResult} />
          )}
        </DialogContent>
        <DialogActions>
          {!dialogDone ? (
            <>
              <Button onClick={closeDialog} disabled={dialogLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAction}
                color={dialogAction === "completed" ? "primary" : "secondary"}
                variant="contained"
                disabled={dialogLoading}
                startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
              >
                {dialogLoading
                  ? "Processing..."
                  : dialogCancelToUser && !dialogUserAlreadyRefunded
                  ? `Mark ${dialogAction === "completed" ? "Completed" : "Declined"} & Refund`
                  : `Mark ${dialogAction === "completed" ? "Completed" : "Declined"}`}
              </Button>
            </>
          ) : (
            <Button onClick={closeDialog} color="primary" variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 700 }}>Category & Status Guide</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Each voucher shows a single category and status derived from its full cancellation history.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" style={{ borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow style={{ backgroundColor: "#e0e0e0" }}>
                  <TableCell style={{ fontWeight: 700, width: "22%", borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd" }}>Category</TableCell>
                  <TableCell style={{ fontWeight: 700, width: "18%", borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd" }}>Status</TableCell>
                  <TableCell style={{ fontWeight: 700, borderBottom: "2px solid #9e9e9e" }}>What it means</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell rowSpan={2} style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd", verticalAlign: "middle" }}>
                    <Chip label="Emailed to Tillo" size="small" style={{ backgroundColor: "#e3f2fd", fontSize: "0.75rem" }} />
                  </TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd", borderRight: "1px solid #bdbdbd" }}><Chip label="Pending" size="small" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd" }}>Email sent to Tillo for manual cancellation. Awaiting their response.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd" }}><Chip label="Declined" size="small" color="secondary" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e" }}>Tillo declined the cancellation request. No refund processed yet.</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell rowSpan={3} style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd", verticalAlign: "middle" }}>
                    <Chip label="Cancelled via Tillo" size="small" style={{ backgroundColor: "#f3e5f5", fontSize: "0.75rem" }} />
                  </TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd", borderRight: "1px solid #bdbdbd" }}><Chip label="Pending" size="small" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd" }}>Tillo confirmed the cancellation (via API or email). User has not been refunded yet.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd", borderRight: "1px solid #bdbdbd" }}><Chip label="Completed" size="small" color="primary" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd" }}>Tillo confirmed and the user has been fully refunded.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd" }}><Chip label="Refund Failed" size="small" color="secondary" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e" }}>Tillo confirmed the cancellation, but the user refund failed (e.g. payment API error).</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell rowSpan={3} style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd", verticalAlign: "middle" }}>
                    <Chip label="Loss Absorbed" size="small" style={{ backgroundColor: "#fff3e0", fontSize: "0.75rem" }} />
                  </TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd", borderRight: "1px solid #bdbdbd" }}><Chip label="Completed" size="small" color="primary" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd" }}>User was refunded directly without Tillo confirming cancellation. Sprive absorbs the loss.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd", borderRight: "1px solid #bdbdbd" }}><Chip label="Refund Failed" size="small" color="secondary" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "1px solid #bdbdbd" }}>Attempted to refund user directly (without Tillo), but the refund failed.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e", borderRight: "1px solid #bdbdbd" }}><Chip label="Pending" size="small" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell style={{ borderBottom: "2px solid #9e9e9e" }}>User was refunded (or refund failed), but an email to Tillo is still pending a response. Awaiting Tillo confirmation.</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ borderRight: "1px solid #bdbdbd", verticalAlign: "middle" }}>
                    <Chip label="Other" size="small" style={{ backgroundColor: "#f5f5f5", fontSize: "0.75rem" }} />
                  </TableCell>
                  <TableCell style={{ borderRight: "1px solid #bdbdbd" }}><Chip label="Pending" size="small" style={{ fontSize: "0.75rem" }} /></TableCell>
                  <TableCell>Tillo API attempt failed. Awaiting admin action (send fallback email or cancel to user).</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CancellationHistoryList;
